import axios from "axios";

// Keeps the signed-in session alive. Access tokens expire after a few days
// (SIMPLE_JWT in settings.py) while the refresh token lasts longer, so when an
// authenticated request comes back 401 we swap the refresh token for a new
// access token and replay the request once. If the refresh token is no good
// either, the session is over: tokens are cleared and listeners (AuthContext)
// are told so the UI signs the user out instead of silently failing.

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
export const REFRESH_URL = `${SERVER_URL}/api/auth/jwt/refresh/`;

// Sign in with a username or an email address. Resolves to
// { access, refresh, user } for AuthContext's login().
export const signInWithPassword = async (username, password) => {
  const { data } = await axios.post(`${SERVER_URL}/api/auth/jwt/create/`, {
    username,
    password,
  });
  const me = await axios.get(`${SERVER_URL}/api/current-user/`, {
    headers: { Authorization: `Bearer ${data.access}` },
  });
  return { access: data.access, refresh: data.refresh, user: me.data };
};

const listeners = { refreshed: new Set(), expired: new Set() };

// Subscribe to "refreshed" ({access, refresh}) or "expired" (no args).
// Returns an unsubscribe function.
export const onAuthEvent = (event, fn) => {
  listeners[event].add(fn);
  return () => listeners[event].delete(fn);
};

const emit = (event, payload) => listeners[event].forEach((fn) => fn(payload));

// Seconds-since-epoch expiry of a JWT, or null if it can't be read.
export const tokenExpiry = (token) => {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === "number" ? exp : null;
  } catch (e) {
    return null;
  }
};

export const isTokenExpired = (token, now = Date.now()) => {
  const exp = tokenExpiry(token);
  return exp !== null && exp * 1000 <= now;
};

export const clearSession = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

const expireSession = () => {
  clearSession();
  emit("expired");
};

// Concurrent 401s (a page often fires several requests at once) share one
// refresh call, so the rotated refresh token is only spent once.
let inFlight = null;

// Swap the stored refresh token for a new access token. Resolves to the new
// access token, or null when the session can't be renewed (it is then ended).
export const refreshAccessToken = () => {
  if (inFlight) return inFlight;
  const refresh = localStorage.getItem("refresh_token");
  if (!refresh || isTokenExpired(refresh)) {
    expireSession();
    return Promise.resolve(null);
  }
  inFlight = axios
    .post(REFRESH_URL, { refresh }, { _skipAuthRefresh: true })
    .then(({ data }) => {
      localStorage.setItem("access_token", data.access);
      // The backend rotates refresh tokens (ROTATE_REFRESH_TOKENS).
      const nextRefresh = data.refresh || refresh;
      localStorage.setItem("refresh_token", nextRefresh);
      emit("refreshed", { access: data.access, refresh: nextRefresh });
      return data.access;
    })
    .catch(() => {
      expireSession();
      return null;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

const sentWithToken = (config) => {
  const headers = config.headers || {};
  const auth =
    typeof headers.get === "function" ? headers.get("Authorization") : headers.Authorization;
  return typeof auth === "string" && auth.startsWith("Bearer ");
};

// Install the 401 → refresh → retry interceptor on an axios instance (the
// default one by default). Only requests that carried a token are retried, and
// each at most once. Returns the interceptor id so tests can eject it.
export const installAuthRefresh = (instance = axios) =>
  instance.interceptors.response.use(undefined, async (error) => {
    const config = error.config;
    if (
      !config ||
      error.response?.status !== 401 ||
      config._skipAuthRefresh ||
      config._authRetried ||
      !sentWithToken(config)
    ) {
      throw error;
    }
    const access = await refreshAccessToken();
    if (!access) throw error;
    config._authRetried = true;
    if (typeof config.headers?.set === "function") {
      config.headers.set("Authorization", `Bearer ${access}`);
    } else {
      config.headers = { ...config.headers, Authorization: `Bearer ${access}` };
    }
    return instance(config);
  });
